import { BOOK_COMPLIANCE, type BookAsk } from './bookAsk'
import { pickOne, pickState, searchOf } from './searchFilter'

/**
 * What a book's own page is narrowed to (TRKC-97): its search, the paperwork's standing,
 * whether we still work with the record, and the state its address is in. Kept in the
 * address, the board's rule (TRKC-90): going back to the book from a record — or opening a
 * shared link — shows the same list.
 *
 * <p>
 * `active` is the working relationship, and inactive records are hidden unless asked for
 * (TRKC-110 item 2): true, the default, is the active ones; false, the inactive ones; absent,
 * all of them. A search finds the inactive ones too (`bookAskOf`).
 * </p>
 */
export type BookFilter = Pick<BookAsk, 'search' | 'compliance' | 'active' | 'state'>

/**
 * A book's filter as its address spells it: `?search=coyote&compliance=Blocked&active=false&state=TX`.
 * No `active` is the default — the active records; `active=false` the inactive ones, and
 * `active=all` every record.
 */
export function bookFilterFrom(search: URLSearchParams): BookFilter {
  const active = search.get('active')

  return {
    search: search.get('search')?.trim() || undefined,
    compliance: pickOne(search.get('compliance'), BOOK_COMPLIANCE),
    active: active === 'false' ? false : active === 'all' ? undefined : true,
    state: pickState(search.get('state')),
  }
}

/** The address a filter is written to; the default relationship is left unsaid. */
export function bookSearchFrom(filter: BookFilter): URLSearchParams {
  return searchOf([
    ['search', filter.search?.trim()],
    ['compliance', filter.compliance],
    ['active', filter.active === true ? undefined : filter.active === false ? 'false' : 'all'],
    ['state', filter.state],
  ])
}

/**
 * What the book is asked for. At its default — *Active* — the relationship holds the inactive
 * records back, but a search finds them too (Lino, 2026-10-15): deactivating a carrier or a
 * broker releases its MC and USDOT, so a company that is only inactive could otherwise be
 * registered again by somebody who searched for it first and found nothing.
 */
export function bookAskOf(filter: BookFilter): BookAsk {
  return { ...filter, active: filter.active === true && filter.search !== undefined ? undefined : filter.active }
}

/**
 * What an empty book says. Narrowed by anything but its default, nothing matched; at its
 * default it holds no active record — which is not the same as holding none at all, so it
 * says where the inactive ones are; asked for everything, the book is empty.
 */
export function emptyBookSays(filter: BookFilter): 'noMatches' | 'emptyActive' | 'empty' {
  const narrowed =
    filter.search !== undefined || filter.compliance !== undefined || filter.state !== undefined || filter.active === false

  if (narrowed) {
    return 'noMatches'
  }

  return filter.active === true ? 'emptyActive' : 'empty'
}

/** Whether a search at the default relationship may have found inactive records, listed after the active ones. */
export function searchFindsInactive(filter: BookFilter): boolean {
  return filter.active === true && filter.search !== undefined
}
